import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { axInst } from "../Login/Service";
import { FaRegChartBar } from "react-icons/fa";
import { GoPerson } from "react-icons/go";
import ServiceCollabaxFilter from "../Filters/ServiceCollabaxFilter";

/* ===== FORMATTER FOR L / CR ===== */
const formatINR = (num) => {
  if (!num && num !== 0) return "-";

  if (num >= 10000000) {
    return (num / 10000000).toFixed(2) + " Cr";
  }

  if (num >= 100000) {
    return (num / 100000).toFixed(2) + " L";
  }

  if (num >= 1000) {
    return (num / 1000).toFixed(2) + " K";
  }

  return num.toLocaleString("en-IN");
};

const fetchServiceCollabaxModal = async (body, endpoint) => {
  const res = await axInst.post(endpoint, body);
  return res.data;
};

const months = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const getYTDValue = (val) =>
  typeof val === "object" && val !== null ? val.value || 0 : val || 0;

const getFYValue = (row, key) => (key ? row[key] || 0 : 0);

const ServiceCollabaxModal = ({
  closeModal,
  Heading,
  modalParams,
  endpoint,
}) => {
  const { data, submitted } = useSelector((e) => e.filtersServiceCollabax);

  const requestBody = {
    ...data,
    ...modalParams,
  };

  const { data: apiData = {}, isFetching } = useQuery({
    queryKey: ["ServiceCollabaxModal", submitted, modalParams, endpoint],
    queryFn: () => fetchServiceCollabaxModal(requestBody, endpoint),
    cacheTime: 0,
    staleTime: 0,
  });

  /* ESC CLOSE */
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [closeModal]);

  /* ===== CALCULATE TOTALS ===== */

  if (isFetching) return null;

  const responseValues = Object.values(apiData || {});
  const responseArray = responseValues.length > 0 ? responseValues[0] : [];

  const hasCards = responseArray?.[0]?.cards !== undefined;
  const tableData = hasCards ? responseArray.slice(1) : responseArray;

  // --- Robust FY Key Discovery ---
  const sampleRows = tableData.slice(0, 5);
  const allSampleKeys = [
    ...new Set(sampleRows.flatMap((row) => Object.keys(row || {}))),
  ];

  const fyKeys = allSampleKeys.filter((k) => k.toUpperCase().includes("FY"));
  // Extract years like '25-26'
  const yearPatterns = fyKeys
    .map((k) => k.match(/\d{2}-\d{2}/)?.[0])
    .filter(Boolean);
  const years = [...new Set(yearPatterns)].sort();

  const prevYear =
    years.length > 1
      ? years[years.length - 2]
      : years.length === 1
        ? years[0]
        : null;
  const currYear = years.length > 1 ? years[years.length - 1] : null;

  const findKey = (year, type) => {
    if (!year) return null;
    return type === "average"
      ? fyKeys.find(
          (k) => k.includes(year) && k.toLowerCase().includes("average"),
        )
      : fyKeys.find(
          (k) => k.includes(year) && !k.toLowerCase().includes("average"),
        );
  };

  const rawFyActualKey = findKey(prevYear, "actual");
  const rawFyAverageKey = findKey(prevYear, "average");

  let rawYtdActualKey = findKey(currYear, "actual");
  let rawYtdAverageKey = findKey(currYear, "average");

  // Fallback to legacy YTD_Actual/YTD_Average if no current year dynamic key is found
  if (!rawYtdActualKey && allSampleKeys.includes("YTD_Actual"))
    rawYtdActualKey = "YTD_Actual";
  if (!rawYtdAverageKey && allSampleKeys.includes("YTD_Average"))
    rawYtdAverageKey = "YTD_Average";

  const displayFyActualKey = rawFyActualKey
    ? rawFyActualKey.replace(/_/g, " ")
    : "FY Actual";
  const displayFyAverageKey = rawFyAverageKey
    ? rawFyAverageKey.replace(/_/g, " ")
    : "FY Average";

  const displayYtdActualKey = rawYtdActualKey
    ? rawYtdActualKey.replace(/_/g, " ")
    : "YTD Actual";
  const displayYtdAverageKey = rawYtdAverageKey
    ? rawYtdAverageKey.replace(/_/g, " ")
    : "YTD Average";
  // -------------------------------

  const totals = tableData.reduce(
    (acc, row) => {
      acc.fyActual += getFYValue(row, rawFyActualKey);
      acc.fyAverage += getFYValue(row, rawFyAverageKey);
      acc.ytdActual += getYTDValue(row[rawYtdActualKey]);
      acc.ytdAverage += getYTDValue(row[rawYtdAverageKey]);

      months.forEach((m) => {
        acc.months[m] += row.month_wise_revenue?.[m] || 0;
      });

      return acc;
    },
    {
      fyActual: 0,
      fyAverage: 0,
      ytdActual: 0,
      ytdAverage: 0,
      months: Object.fromEntries(months.map((m) => [m, 0])),
    },
  );

  const visibleColumns = {
    fyAverage: !!rawFyAverageKey,
    fyActual: !!rawFyActualKey,
    ytdAverage: !!rawYtdAverageKey,
    ytdActual: !!rawYtdActualKey,
    months: Object.fromEntries(
      months.map((m) => [
        m,
        tableData.some((row) => {
          const val = row.month_wise_revenue?.[m];
          return val !== "" && val !== null && val !== undefined;
        }),
      ]),
    ),
  };

  const visibleMonthCount = Object.values(visibleColumns.months).filter(
    Boolean,
  ).length;

  const isAverageModal = Boolean(modalParams?.sso_status);
  const rowCount = tableData.length || 1;
  const displayValue = (val) =>
    formatINR(isAverageModal ? val / rowCount : val);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg shadow-xl border w-[97%] max-w-[1238px] h-[500px] p-3 relative">
        <div className="text-lg font-semibold mb-2 flex items-center text-gray-700">
          <FaRegChartBar className="mr-2" />
          {Heading}
        </div>

        <button
          onClick={closeModal}
          className="absolute top-2 right-3 text-2xl text-gray-500"
        >
          &times;
        </button>

        <div className="mb-3">
          <ServiceCollabaxFilter
            fields={["Region", "RSM", "Dealer Code", "LOB"]}
            filtermodal={closeModal}
          />
        </div>

        {/* ================= TABLE ================= */}
        <div className="flex-1 min-h-0  h-[200px] md:h-[300px] overflow-y-auto justify-items-center justify-center border rounded-md pr-2">
          <table className=" text-xs border-collapse text-center">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#f1f5f9] text-[#475569]">
                <th rowSpan="2" className="p-2 border">
                  Region
                </th>
                <th rowSpan="2" className="p-2 border">
                  RSM
                </th>
                <th rowSpan="2" className="p-2 border">
                  SSO
                </th>

                {visibleColumns.fyAverage && (
                  <th rowSpan="2" className="p-2 border">
                    {displayFyAverageKey}
                  </th>
                )}
                {visibleColumns.fyActual && (
                  <th rowSpan="2" className="p-2 border">
                    {displayFyActualKey}
                  </th>
                )}

                {visibleMonthCount > 0 && (
                  <th colSpan={visibleMonthCount} className=""></th>
                )}

                {visibleColumns.ytdAverage && (
                  <th rowSpan="2" className="p-2 border ">
                    {displayYtdAverageKey}
                  </th>
                )}
                {visibleColumns.ytdActual && (
                  <th rowSpan="2" className="p-2 border ">
                    {displayYtdActualKey}
                  </th>
                )}
              </tr>

              <tr className="bg-[#f1f5f9] text-[#475569]">
                {months.map(
                  (m) =>
                    visibleColumns.months[m] && (
                      <th key={m} className="p-1 font-semibold">
                        {m}
                      </th>
                    ),
                )}
              </tr>
            </thead>

            <tbody>
              {tableData.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="border p-2 text-[#7e22ce]">
                    <span className="font-bold px-2 py-1 bg-[#ecd7fe] rounded-sm">
                      {row.region_name}
                    </span>
                  </td>

                  <td className="border p-2 flex items-center text-center justify-center text-[#878788]">
                    <span className="mr-1 text-[#b6b5b7]">
                      <GoPerson />
                    </span>
                    {row.rsm}
                  </td>

                  <td className="border p-2">{row.sso_location}</td>

                  {visibleColumns.fyAverage && (
                    <td className="border p-2  font-semibold text-[#4f46e5] whitespace-nowrap">
                      {formatINR(getFYValue(row, rawFyAverageKey))}
                    </td>
                  )}

                  {visibleColumns.fyActual && (
                    <td className="border p-2  font-semibold text-[#4f46e5] whitespace-nowrap">
                      {formatINR(getFYValue(row, rawFyActualKey))}
                    </td>
                  )}

                  {months.map(
                    (month) =>
                      visibleColumns.months[month] && (
                        <td
                          key={month}
                          className="p-1 text-center border border-b"
                        >
                          <span className="bg-[#b5e9d8] p-1 rounded-sm inline-block w-[50px] whitespace-nowrap">
                            {formatINR(row.month_wise_revenue?.[month])}
                          </span>
                        </td>
                      ),
                  )}

                  {visibleColumns.ytdAverage && (
                    <td className="border p-2  font-semibold text-[#878788]">
                      {formatINR(getYTDValue(row[rawYtdAverageKey]))}
                    </td>
                  )}
                  {visibleColumns.ytdActual && (
                    <td className="border p-2  font-semibold text-[#878788]">
                      {formatINR(getYTDValue(row[rawYtdActualKey]))}
                    </td>
                  )}
                </tr>
              ))}

              {/* ===== TOTAL ROW ===== */}
              <tr className=" bg-[#4243ac] font-bold sticky bottom-0 text-center text-white whitespace-nowrap">
                <td className="border p-2 ">Pan India</td>
                <td className="border p-2 ">
                  {isAverageModal ? "AVERAGE" : "TOTAL"}
                </td>
                <td className="border p-2 ">Pan India</td>
                {visibleColumns.fyAverage && (
                  <td className="border p-2 ">
                    {displayValue(totals.fyAverage)}
                  </td>
                )}
                {visibleColumns.fyActual && (
                  <td className="border p-2 ">
                    {displayValue(totals.fyActual)}
                  </td>
                )}
                {months.map(
                  (m) =>
                    visibleColumns.months[m] && (
                      <td key={m} className="p-2 text-center">
                        {displayValue(totals.months[m])}
                      </td>
                    ),
                )}
                {visibleColumns.ytdAverage && (
                  <td className="border p-2 ">
                    {displayValue(totals.ytdAverage)}
                  </td>
                )}
                {visibleColumns.ytdActual && (
                  <td className="border p-2 ">
                    {displayValue(totals.ytdActual)}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceCollabaxModal;
